import React from 'react';
import { getImageSetsBySource, getImageSizesByType, RESPONSIVE_TYPE } from '../Responsive';
import { ResponsiveType } from '../Image';
import { Image as ImageType, isContentImage } from '../../../../types/api/ImageType';
import { Img } from './Img';
import { ImageWithoutAvailableWidths } from '../useImage';
import { ImageModifier } from '../Image.modifier';
import Bem from '../../../../lib/bemUtils/bem';

type Picture = {
  image: ImageType;
  responsiveType?: ResponsiveType;
  modifier?: ImageModifier;
  lazyLoading?: boolean;
  getAlt: () => string;
  getWidthWithQueryParameter: (maxSize?: number) => number | undefined;
  getAvailableWidthOfImage: (imageObj: ImageWithoutAvailableWidths) => number[];
};
/**
 *
 *   What happens here? (Please read this first, before changing stuff!)
 *
 *   - The <picture> contains a fallback <img> with a static src.
 *   - This forces IE11 to load this fallback <img> initially (1024).
 *   - In smaller breakpoints IE11 keeps 1024 image.
 *   - In higher breakpoints it will additionally load the specified image size.
 *   - All modern browsers load the srcSet and ignore the src here (HTML spec).
 *
 *   For details see https://confluence.seitenbau.net/display/INTRAMO/Entwurfsentscheidung%3A+Responsive+Images
 */
export function Picture({
  image,
  responsiveType,
  modifier,
  lazyLoading,
  getAlt,
  getWidthWithQueryParameter,
  getAvailableWidthOfImage,
}: Picture) {
  const maxWidth =
    responsiveType === RESPONSIVE_TYPE.topHeadlineImageSmall
      ? Math.min(...getAvailableWidthOfImage(image as ImageWithoutAvailableWidths))
      : Math.max(...getAvailableWidthOfImage(image as ImageWithoutAvailableWidths));

  return (
    <picture className={Bem('image__picture', modifier)}>
      <source
        srcSet={getImageSetsBySource(image.src, maxWidth)}
        sizes={getImageSizesByType(
          responsiveType,
          responsiveType === RESPONSIVE_TYPE.contentImage && isContentImage(image) ? image.size : null,
        )}
        type={image.contentType}
      />
      <Img
        image={image}
        maxSize={maxWidth}
        modifier={modifier}
        lazyLoading={lazyLoading}
        getWidthWithQueryParameter={getWidthWithQueryParameter}
        getAlt={getAlt}
      />
    </picture>
  );
}
